import type { FeedVitals } from '../shared/types.js';

/**
 * Client-side mechanical analysis.
 *
 * This runs on the publisher's phone on purpose, not to save the server work
 * but because the phone is the only place the data exists: encoder stats and
 * the raw pre-encode frames never leave the device. It also means 30 phones
 * cost the server exactly what 3 do.
 *
 * Budget matters. A phone in a warm gym is already encoding H.264; if we spend
 * real CPU here it thermally throttles and we degrade the feed we are measuring.
 * So: a 64x36 luma sample four times a second, and one getStats() call a second.
 */

const SAMPLE_W = 64;
const SAMPLE_H = 36;
const PICTURE_HZ = 4;
const SHAKE_WINDOW = 12; // ~3 seconds of motion samples

export interface VitalsSource {
  pc: RTCPeerConnection;
  video: HTMLVideoElement;
  stream: MediaStream;
}

export class VitalsCollector {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private prev: Float32Array | null = null;

  private luma = 0;
  private clipping = 0;
  private motion = 0;
  private motionHistory: number[] = [];

  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  // TS 5.7 distinguishes the backing buffer; getFloatTimeDomainData wants a
  // plain ArrayBuffer view, never a SharedArrayBuffer one.
  private audioBuf: Float32Array<ArrayBuffer> | null = null;
  private audioTrack: MediaStreamTrack | null = null;

  private lastBytes = 0;
  private lastStatsAt = 0;
  private bitrate = 0;
  private packetLoss = 0;
  private rttMs: number | null = null;
  private fps = 0;
  private frameWidth = 0;
  private frameHeight = 0;
  private freezeSeconds = 0;
  private qualityLimitationReason = 'none';

  private batteryLevel: number | null = null;
  /**
   * Wall clock when the most recent frame was captured, from the browser.
   *
   * `captureTime` is the field that makes metadata alignment possible: it is
   * stamped at capture, so it is unaffected by encoder buffering or transport
   * delay, which were two of the three reasons the proposal originally gave for
   * refusing to align on timestamps at all.
   *
   * Null on any browser without `requestVideoFrameCallback` or without
   * `captureTime` in its metadata, which is not a failure. A null feed falls
   * back to correlating audio over the full offset range.
   */
  private capturedAt: number | null = null;
  private frameCallbackHandle: number | null = null;
  private timers: number[] = [];
  private readonly startedAt = performance.now();

  constructor(private readonly src: VitalsSource) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = SAMPLE_W;
    this.canvas.height = SAMPLE_H;
    this.watchCaptureTime();
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('2d canvas unavailable');
    this.ctx = ctx;
  }

  async start(): Promise<void> {
    this.attachAudio();
    void this.readBattery();
    this.timers.push(
      window.setInterval(() => this.samplePicture(), 1000 / PICTURE_HZ),
      window.setInterval(() => void this.sampleTransport(), 1000),
    );
  }

  stop(): void {
    for (const t of this.timers) window.clearInterval(t);
    this.timers = [];
    // The capture-time watcher re-registers itself every frame, so clearing
    // the timers is not stopping the collector: the handle below was stored
    // and never read, and the Stop control shipped leaving this callback
    // running for the life of the video element. A write-only local is
    // invisible to the dead-export gate; `noUnusedLocals` found it the day it
    // was enabled.
    const video = this.src.video as HTMLVideoElement & {
      cancelVideoFrameCallback?: (handle: number) => void;
    };
    if (this.frameCallbackHandle !== null) {
      video.cancelVideoFrameCallback?.(this.frameCallbackHandle);
      this.frameCallbackHandle = null;
    }
    void this.audioCtx?.close();
  }

  // --- picture ---------------------------------------------------------------

  private samplePicture(): void {
    const { video } = this.src;
    if (video.readyState < 2 || video.videoWidth === 0) return;

    this.ctx.drawImage(video, 0, 0, SAMPLE_W, SAMPLE_H);
    const { data } = this.ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H);

    const n = SAMPLE_W * SAMPLE_H;
    const cur = new Float32Array(n);
    let sum = 0;
    let clipped = 0;

    for (let i = 0, p = 0; i < n; i++, p += 4) {
      // Rec. 601 luma is close enough at this sample size and avoids a matrix.
      const y = (0.299 * data[p]! + 0.587 * data[p + 1]! + 0.114 * data[p + 2]!) / 255;
      cur[i] = y;
      sum += y;
      if (y <= 0.02 || y >= 0.98) clipped++;
    }

    this.luma = sum / n;
    this.clipping = clipped / n;

    if (this.prev) {
      let diff = 0;
      for (let i = 0; i < n; i++) diff += Math.abs(cur[i]! - this.prev[i]!);
      this.motion = diff / n;
      this.motionHistory.push(this.motion);
      if (this.motionHistory.length > SHAKE_WINDOW) this.motionHistory.shift();
    }
    this.prev = cur;
  }

  /**
   * Shake is the variance of recent motion, not motion itself. A locked-off
   * camera on a fast break is high motion and low shake; a handheld phone
   * tracking nothing is the opposite. Only one of those is unwatchable.
   */
  private get shake(): number {
    const h = this.motionHistory;
    if (h.length < 3) return 0;
    const mean = h.reduce((a, b) => a + b, 0) / h.length;
    const variance = h.reduce((a, b) => a + (b - mean) ** 2, 0) / h.length;
    return Math.sqrt(variance);
  }

  // --- sound -----------------------------------------------------------------

  private attachAudio(): void {
    const track = this.src.stream.getAudioTracks()[0];
    if (!track) return;
    this.audioTrack = track;
    try {
      this.audioCtx = new AudioContext();
      const source = this.audioCtx.createMediaStreamSource(new MediaStream([track]));
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 2048;
      source.connect(this.analyser);
      this.audioBuf = new Float32Array(new ArrayBuffer(this.analyser.fftSize * 4));
    } catch {
      this.analyser = null;
    }
  }

  /**
   * Is the microphone actually capturing?
   *
   * Three ways it is not, and none of them is "the room is quiet": no track was
   * ever offered, the track has ended, or the source has stopped delivering
   * samples (`muted`, which on a getUserMedia track means the browser is not
   * getting audio, not that the operator lowered anything). This is what the
   * hard gate reads, so the level is free to be low.
   */
  private audioLive(): boolean {
    const track = this.audioTrack;
    if (!track) return false;
    return track.readyState === 'live' && !track.muted;
  }

  private readAudio(): { rms: number; clipping: number } {
    if (!this.analyser || !this.audioBuf) return { rms: 0, clipping: 0 };
    this.analyser.getFloatTimeDomainData(this.audioBuf);
    let sumSquares = 0;
    let clipped = 0;
    for (let i = 0; i < this.audioBuf.length; i++) {
      const s = this.audioBuf[i]!;
      sumSquares += s * s;
      if (Math.abs(s) >= 0.99) clipped++;
    }
    return {
      rms: Math.sqrt(sumSquares / this.audioBuf.length),
      clipping: clipped / this.audioBuf.length,
    };
  }

  // --- transport -------------------------------------------------------------

  /**
   * `captureTime` is in `VideoFrameCallbackMetadata` but not in every browser's
   * lib.dom typings, and `requestVideoFrameCallback` is not on every
   * HTMLVideoElement. Both are feature-detected rather than assumed, and both
   * failure paths leave `capturedAt` null.
   */
  private watchCaptureTime(): void {
    const video = this.src.video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: (now: number, meta: Record<string, number>) => void) => number;
    };
    if (typeof video.requestVideoFrameCallback !== 'function') return;
    const tick = (_now: number, meta: Record<string, number>): void => {
      const capture = meta['captureTime'];
      // performance.timeOrigin puts a DOMHighResTimeStamp on the same wall
      // clock the server compares against. Without it the number is only
      // meaningful inside this page.
      this.capturedAt = typeof capture === 'number' ? performance.timeOrigin + capture : null;
      this.frameCallbackHandle = video.requestVideoFrameCallback!(tick);
    };
    this.frameCallbackHandle = video.requestVideoFrameCallback(tick);
  }

  private async sampleTransport(): Promise<void> {
    let report: RTCStatsReport;
    try {
      report = await this.src.pc.getStats();
    } catch {
      return;
    }

    const now = performance.now();

    report.forEach((s: any) => {
      if (s.type === 'outbound-rtp' && s.kind === 'video') {
        if (this.lastStatsAt > 0) {
          const dt = (now - this.lastStatsAt) / 1000;
          if (dt > 0) this.bitrate = ((s.bytesSent - this.lastBytes) * 8) / dt;
        }
        this.lastBytes = s.bytesSent ?? 0;
        this.fps = s.framesPerSecond ?? 0;
        this.frameWidth = s.frameWidth ?? 0;
        this.frameHeight = s.frameHeight ?? 0;
        this.qualityLimitationReason = s.qualityLimitationReason ?? 'none';
      }
      if (s.type === 'remote-inbound-rtp' && s.kind === 'video') {
        this.packetLoss = typeof s.fractionLost === 'number' ? s.fractionLost : this.packetLoss;
        this.rttMs = typeof s.roundTripTime === 'number' ? s.roundTripTime * 1000 : this.rttMs;
      }
    });

    // The sender has no totalFreezesDuration, that stat lives on the receiver.
    // Accumulate our own: any second under 5fps is a second the viewer lost.
    if (this.lastStatsAt > 0 && this.fps < 5) {
      this.freezeSeconds += (now - this.lastStatsAt) / 1000;
    }
    this.lastStatsAt = now;
  }

  private async readBattery(): Promise<void> {
    try {
      const nav = navigator as any;
      if (typeof nav.getBattery !== 'function') return;
      const b = await nav.getBattery();
      this.batteryLevel = b.level;
      b.addEventListener('levelchange', () => (this.batteryLevel = b.level));
    } catch {
      this.batteryLevel = null;
    }
  }

  /** No web API reports thermal state, so infer it from the encoder giving up. */
  private get thermalHint(): FeedVitals['thermalHint'] {
    if (this.qualityLimitationReason === 'cpu') return 'throttled';
    if (this.qualityLimitationReason === 'none' || this.qualityLimitationReason === 'bandwidth') {
      return 'nominal';
    }
    return 'unknown';
  }

  snapshot(): FeedVitals {
    const audio = this.readAudio();
    return {
      t: performance.now() - this.startedAt,
      wall: Date.now(),
      capturedAt: this.capturedAt,
      bitrate: this.bitrate,
      packetLoss: this.packetLoss,
      rttMs: this.rttMs,
      fps: this.fps,
      frameWidth: this.frameWidth,
      frameHeight: this.frameHeight,
      freezeSeconds: this.freezeSeconds,
      qualityLimitationReason: this.qualityLimitationReason,
      luma: this.luma,
      clipping: this.clipping,
      motion: this.motion,
      shake: this.shake,
      audioTrackLive: this.audioLive(),
      audioRms: audio.rms,
      audioClipping: audio.clipping,
      batteryLevel: this.batteryLevel,
      thermalHint: this.thermalHint,
    };
  }
}
